import React, { Suspense, useEffect } from 'react'
import { NotFound, Route, Switch, useHistory, useLocation } from '@fs/zion-router'
import ErrorBoundary from '@fs/zion-error-boundary'
import { i18n } from '@fs/zion-locale'
import zionDebug from '@fs/zion-debug'
import OrdinanceReadyPageSkeleton from './components/OrdinancesReadyPage/OrdinanceReadyPageSkeleton'
import languageUrlMap from './languageUrlMap.json'

const debug = zionDebug('church:ordinance-ready:app')

const AddWhatYouKnowPage = React.lazy(() => import('./components/AddWhatYouKnowPage/AddWhatYouKnowPage'))
const ChurchLeadersPage = React.lazy(() => import('./components/ChurchLeadersPage/ChurchLeadersPage'))
const ConsultantPage = React.lazy(() => import('./components/ConsultantPage/ConsultantPage'))
const OrdinancesReadyPage = React.lazy(() => import('./components/OrdinancesReadyPage/OrdinancesReadyPage'))

type LanguageUrlMap = Record<string, string>

const getOrdinancesReadyRoute = (locale: string): string => {
  return (languageUrlMap as LanguageUrlMap)[locale] || (languageUrlMap as LanguageUrlMap).en
}

function LocaleRedirector(): null {
  const location = useLocation()
  const history = useHistory()
  const language = i18n.language || 'en'

  useEffect(() => {
    const pathSegments = location.pathname.split('/').filter(Boolean)
    const lastSegment = pathSegments.at(-1)

    let isOrdinancePath = false
    let matchingKey: string | null = null

    Object.entries(languageUrlMap as LanguageUrlMap).forEach(([key, path]) => {
      const segments = path.split('/').filter(Boolean)
      const pathLastSegment = segments.at(-1)

      if (lastSegment === pathLastSegment) {
        isOrdinancePath = true
        matchingKey = key
      }
    })

    if (isOrdinancePath && matchingKey !== language) {
      const targetPath = (languageUrlMap as LanguageUrlMap)[language] || (languageUrlMap as LanguageUrlMap).en

      if (location.pathname !== targetPath) {
        debug('Redirecting to correct language path', {
          from: location.pathname,
          to: targetPath,
          currentLanguage: language,
        })

        history.replace(targetPath)
      }
    }
  }, [location.pathname, language, history])

  return null
}

function App(): JSX.Element {
  const ordinancesReadyRoute = getOrdinancesReadyRoute(i18n?.language || 'en')

  if (process.env.NODE_ENV !== 'production') {
    debug('App rendering with:', { ordinancesReadyRoute })
  }

  const browserUrlHasTrailingSlash = (): boolean => {
    if (globalThis.window !== undefined) {
      const fullUrl = globalThis.window.location.pathname
      debug('Browser URL pathname:', fullUrl)

      const ordinancesUrls = Object.values(languageUrlMap as LanguageUrlMap)
      const matchingRoute = ordinancesUrls.find((route) => fullUrl === `/en/church${route}/`)

      if (matchingRoute) {
        debug('Found trailing slash in browser URL for route:', matchingRoute)
        return true
      }
    }
    return false
  }

  const shouldShowNotFound = browserUrlHasTrailingSlash()

  const ordinancesTrailingSlashRoutes = Object.values(languageUrlMap as LanguageUrlMap).map((route) => `${route}/`)

  if (process.env.NODE_ENV !== 'production') {
    debug('Generated trailing slash routes:', ordinancesTrailingSlashRoutes)
    debug('Current ordinancesReadyRoute:', ordinancesReadyRoute)
    debug('Should show NotFound based on browser URL:', shouldShowNotFound)
  }

  return (
    <ErrorBoundary>
      <Suspense fallback={<OrdinanceReadyPageSkeleton />}>
        <LocaleRedirector />
        <Switch>
          <Route exact path="/" component={NotFound} />

          {/* Check browser URL for trailing slash and show NotFound if present */}
          {shouldShowNotFound && (
            <Route
              exact
              path={ordinancesReadyRoute}
              render={() => {
                if (process.env.NODE_ENV !== 'production') {
                  debug('MATCHED NotFound route due to trailing slash in browser URL')
                }
                return <NotFound />
              }}
            />
          )}

          {!shouldShowNotFound && (
            <Route
              exact
              key={ordinancesReadyRoute}
              path={ordinancesReadyRoute}
              render={() => {
                if (process.env.NODE_ENV !== 'production') {
                  debug('MATCHED main OrdinancesReadyPage route:', ordinancesReadyRoute)
                }
                return <OrdinancesReadyPage />
              }}
            />
          )}

          <Route exact path="/add-what-you-know" component={AddWhatYouKnowPage} />
          <Route path="/leaders/" component={ChurchLeadersPage} />
          <Route exact path="/consultant" component={ConsultantPage} />
          <Route component={NotFound} />
        </Switch>
      </Suspense>
    </ErrorBoundary>
  )
}

export default App
